<template>
  <div class="app-view">
    <v-container fluid class="evaluation-summary-view">
      <!-- Loading State -->
      <v-progress-linear v-if="isLoading" indeterminate color="primary" class="mb-4"></v-progress-linear>

      <!-- Error State -->
      <v-alert v-if="error" type="error" dismissible @click="error = ''" class="mb-4">
        {{ error }}
      </v-alert>

      <!-- Summary Content -->
      <div v-if="!isLoading && summaryData">
        <!-- Header Section -->
        <v-card class="mb-6 summary-header-card" elevation="2">
          <v-card-text class="pa-6">
            <div class="d-flex align-center mb-4">
              <v-icon x-large color="primary" class="mr-4">mdi-file-document-outline</v-icon>
              <div class="flex-grow-1">
                <h1 class="text-h4 font-weight-bold primary--text">{{ summaryData.title }}</h1>
                <div class="mt-2">
                  <v-chip
                    :color="getStatusColor(summaryData.status)"
                    small
                    outlined
                    class="mr-2"
                  >
                    {{ summaryData.status }}
                  </v-chip>
                  <span class="text-subtitle-1 text--secondary">
                    Evaluation Period: {{ formatDate(summaryData.startDate) }} - {{ formatDate(summaryData.endDate) }}
                  </span>
                </div>
              </div>
              <div class="summary-actions">
                <!-- Single document or no documents: simple button -->
                <v-btn
                  v-if="availableDocuments.length <= 1"
                  color="primary"
                  outlined
                  class="mr-2"
                  :loading="downloadingDocument"
                  @click="exportToPDF()"
                >
                  <v-icon left>mdi-file-pdf-box</v-icon>
                  Export PDF
                </v-btn>

                <!-- Multiple documents: dropdown menu -->
                <v-menu v-else offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      color="primary"
                      outlined
                      class="mr-2"
                      v-bind="attrs"
                      v-on="on"
                      :loading="downloadingDocument"
                    >
                      <v-icon left>mdi-file-pdf-box</v-icon>
                      Export PDF
                      <v-icon right>mdi-chevron-down</v-icon>
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item
                      v-for="doc in availableDocuments"
                      :key="doc.id"
                      @click="exportToPDF(doc.id)"
                    >
                      <v-list-item-icon>
                        <v-icon>mdi-file-document</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>{{ doc.documentName }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-menu>

                <v-btn color="primary" @click="printSummary">
                  <v-icon left>mdi-printer</v-icon>
                  Print
                </v-btn>
              </div>
            </div>

            <v-divider class="my-4"></v-divider>

            <v-row>
              <v-col cols="12" sm="6" md="3">
                <div class="info-item">
                  <div class="text-caption text--secondary">Evaluatee</div>
                  <div class="text-h6">{{ summaryData.evaluateeName }}</div>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <div class="info-item">
                  <div class="text-caption text--secondary">Submission Date</div>
                  <div class="text-h6">{{ summaryData.submittedDate ? formatDate(summaryData.submittedDate) : 'Not Submitted' }}</div>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <div class="info-item">
                  <div class="text-caption text--secondary">Total Sections</div>
                  <div class="text-h6">{{ Object.keys(summaryData.sectionResponses || {}).length }}</div>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <div class="info-item">
                  <div class="text-caption text--secondary">Total Responses</div>
                  <div class="text-h6">{{ getTotalResponses() }}</div>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Score Card -->
        <v-card v-if="summaryData.totalScore != null"
                class="mb-6 score-card"
                elevation="3">
          <v-card-text class="text-center py-8">
            <div class="score-display">
              <div class="text-h1 font-weight-bold score-value">{{ summaryData.totalScore.toFixed(1) }}</div>
              <div class="text-h5 mt-2">Total Score</div>
              <div class="text-subtitle-1 mt-1" style="color: rgba(255,255,255,0.85)">
                out of {{ summaryData.maxPossibleScore.toFixed(1) }}
              </div>
              <v-progress-linear
                :value="summaryData.scorePercentage"
                height="12"
                rounded
                color="white"
                background-color="rgba(255,255,255,0.3)"
                class="mt-4 mx-auto"
                style="max-width: 300px"
              ></v-progress-linear>
              <div class="text-h6 mt-2" style="color: rgba(255,255,255,0.9)">
                {{ summaryData.scorePercentage.toFixed(1) }}%
              </div>
            </div>
          </v-card-text>
        </v-card>

        <!-- Section Responses -->
        <v-card class="section-responses-card" elevation="1">
          <v-card-title class="text-h5">
            <v-icon left color="primary">mdi-format-list-bulleted</v-icon>
            Section Responses
          </v-card-title>
          <v-card-text>
            <v-expansion-panels v-if="summaryData.sectionResponses" accordion multiple>
              <v-expansion-panel
                v-for="(responses, sectionName, index) in summaryData.sectionResponses"
                :key="sectionName"
                :default-open="index === 0"
              >
                <v-expansion-panel-header class="section-panel-header">
                  <div class="d-flex align-center">
                    <v-icon left color="primary">mdi-folder-outline</v-icon>
                    <span class="text-h6 font-weight-medium">{{ sectionName }}</span>
                    <v-spacer></v-spacer>
                    <v-chip
                      v-if="getSectionScore(sectionName) !== null && getSectionScore(sectionName) !== undefined"
                      small
                      color="amber darken-2"
                      outlined
                      class="mr-2"
                    >
                      <v-icon left small>mdi-star</v-icon>
                      Score: {{ getSectionScore(sectionName).toFixed(1) }}
                    </v-chip>
                    <v-chip small color="primary" outlined>
                      {{ responses.length }} response{{ responses.length !== 1 ? 's' : '' }}
                    </v-chip>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div class="section-content">
                    <div v-for="(response, idx) in responses" :key="idx" class="response-card mb-4">
                      <v-card outlined>
                        <v-card-text>
                          <div class="response-header mb-3">
                            <div class="text-subtitle-1 font-weight-bold">{{ response.fieldLabel }}</div>
                            <v-chip x-small label color="grey lighten-2">{{ response.fieldType }}</v-chip>
                          </div>

                          <div class="response-body">
                            <!-- Text/Textarea Response -->
                            <div v-if="response.fieldType === 'TEXT' || response.fieldType === 'TEXTAREA'"
                                 class="text-body-1 response-text">
                              {{ response.response || 'No response provided' }}
                            </div>

                            <!-- Multi-Text Response -->
                            <div v-else-if="response.fieldType === 'MULTI_TEXT'" class="multi-text-response">
                              <v-list dense>
                                <v-list-item v-for="(item, itemIdx) in response.response" :key="itemIdx">
                                  <v-list-item-content>
                                    <v-list-item-title class="font-weight-medium">{{ item.label }}</v-list-item-title>
                                    <v-list-item-subtitle class="text-wrap">{{ item.value }}</v-list-item-subtitle>
                                  </v-list-item-content>
                                </v-list-item>
                              </v-list>
                            </div>

                            <!-- Number Response -->
                            <div v-else-if="response.fieldType === 'NUMBER'" class="text-h5 primary--text">
                              {{ response.response }}
                            </div>

                            <!-- Rating Response -->
                            <div v-else-if="response.fieldType === 'RATING'" class="rating-response">
                              <v-rating
                                :value="response.response"
                                readonly
                                color="amber"
                                background-color="grey lighten-2"
                                size="25"
                                dense
                              ></v-rating>
                              <span class="ml-3 text-h6">{{ response.response }}/{{ response.maxRating || 5 }}</span>
                            </div>

                            <!-- Date Response -->
                            <div v-else-if="response.fieldType === 'DATE'" class="text-body-1">
                              <v-icon small left>mdi-calendar</v-icon>
                              {{ formatDate(response.response) }}
                            </div>

                            <!-- Single Select Response -->
                            <div v-else-if="response.fieldType === 'SINGLE_SELECT' || response.fieldType === 'RADIO'">
                              <v-chip color="primary" outlined>{{ response.response }}</v-chip>
                            </div>

                            <!-- Multi Select Response -->
                            <div v-else-if="response.fieldType === 'MULTI_SELECT' || response.fieldType === 'CHECKBOX'">
                              <v-chip v-for="(option, optIdx) in response.response"
                                      :key="optIdx"
                                      color="primary"
                                      outlined
                                      small
                                      class="mr-2 mb-2">
                                {{ option }}
                              </v-chip>
                            </div>

                            <!-- Multi-Field Response -->
                            <div v-else-if="response.fieldType === 'MULTI_FIELD'" class="multi-field-response">
                              <v-card v-for="(entry, entryIdx) in response.response"
                                      :key="entryIdx"
                                      outlined
                                      class="mb-3 pa-3"
                                      color="grey lighten-5">
                                <v-simple-table dense>
                                  <template v-slot:default>
                                    <tbody>
                                      <tr v-for="(value, key) in entry.subFieldValues" :key="key">
                                        <td class="font-weight-medium">{{ key }}</td>
                                        <td>
                                          <span v-if="Array.isArray(value)">{{ value.join(', ') }}</span>
                                          <span v-else>{{ value }}</span>
                                        </td>
                                      </tr>
                                    </tbody>
                                  </template>
                                </v-simple-table>
                              </v-card>
                            </div>
                          </div>

                          <!-- Comment if exists -->
                          <div v-if="response.comment" class="mt-3 comment-section">
                            <v-divider class="mb-3"></v-divider>
                            <div class="text-caption text--secondary mb-1">Additional Comments</div>
                            <div class="text-body-2 font-italic comment-text">{{ response.comment }}</div>
                          </div>
                        </v-card-text>
                      </v-card>
                    </div>
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-card-text>
        </v-card>

        <!-- Action Buttons -->
        <div class="evaluation-actions mt-6 text-center">
          <v-btn color="primary" outlined rounded small class="mr-3" @click="goBack">
            <v-icon left>mdi-arrow-left</v-icon>
            Back to Evaluation
          </v-btn>

          <!-- Single document or no documents: simple button -->
          <v-btn
            v-if="availableDocuments.length <= 1"
            color="success"
            rounded
            small
            :loading="downloadingDocument"
            @click="exportToPDF()"
          >
            <v-icon left>mdi-download</v-icon>
            Download Summary
          </v-btn>

          <!-- Multiple documents: dropdown menu -->
          <v-menu v-else offset-y top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                color="success"
                rounded
                small
                v-bind="attrs"
                v-on="on"
                :loading="downloadingDocument"
              >
                <v-icon left>mdi-download</v-icon>
                Download Summary
                <v-icon right small>mdi-chevron-up</v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item
                v-for="doc in availableDocuments"
                :key="doc.id"
                @click="exportToPDF(doc.id)"
              >
                <v-list-item-icon>
                  <v-icon>mdi-file-document</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>{{ doc.documentName }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>
    </v-container>
  </div>
</template>

<script>
import Vue from 'vue';
import { getEvaluationSummary } from '@/shared/apis/aper/aperEvaluations';
import { formatDateForDisplay } from '@/utils/dateUtils.js';
import { getDocumentsByTemplate, downloadEvaluationDocument } from '@/shared/apis/aper/aperDocuments';
import { getSectionResponses } from '@/shared/apis/aper/aperSectionResponses';

export default Vue.extend({
  name: 'EvaluationSummaryView',
  props: {
    id: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      summaryData: null,
      isLoading: false,
      error: '',
      availableDocuments: [],
      loadingDocuments: false,
      downloadingDocument: false,
      showDocumentMenu: false,
      sectionScores: new Map()
    };
  },
  async created() {
    await this.fetchSummary();
    await this.fetchSectionScores();
    if (this.summaryData?.templateId) {
      await this.fetchAvailableDocuments();
    }
  },
  methods: {
    async fetchSummary() {
      this.isLoading = true;
      this.error = '';
      try {
        const response = await getEvaluationSummary(this.id);
        this.summaryData = response.data;
      } catch (error) {
        console.error('Error fetching evaluation summary:', error);
        this.error = 'Failed to load evaluation summary. Please try again.';
      } finally {
        this.isLoading = false;
      }
    },

    async fetchSectionScores() {
      try {
        const sectionResponses = await getSectionResponses(Number(this.id));
        sectionResponses.forEach(sr => {
          this.sectionScores.set(sr.sectionName, sr.score);
        });
      } catch (error) {
        console.log('Section scores not available:', error);
      }
    },

    getSectionScore(sectionName) {
      return this.sectionScores.get(sectionName);
    },

    async fetchAvailableDocuments() {
      if (!this.summaryData?.templateId) return;
      this.loadingDocuments = true;
      try {
        const response = await getDocumentsByTemplate(this.summaryData.templateId);
        this.availableDocuments = response.data || [];
      } catch (error) {
        console.error('Error fetching documents:', error);
      } finally {
        this.loadingDocuments = false;
      }
    },

    getStatusColor(status) {
      const statusColors = {
        'DRAFT': 'grey',
        'IN_PROGRESS': 'blue',
        'SUBMITTED': 'orange',
        'REVIEWED': 'purple',
        'APPROVED': 'success',
        'REJECTED': 'error'
      };
      return statusColors[status] || 'grey';
    },

    formatDate(dateValue) {
      return formatDateForDisplay(dateValue);
    },

    getTotalResponses() {
      if (!this.summaryData || !this.summaryData.sectionResponses) return 0;
      return Object.values(this.summaryData.sectionResponses)
        .reduce((total, responses) => total + responses.length, 0);
    },

    goBack() {
      this.$router.push(`/evaluation/evaluations/${this.id}`);
    },

    printSummary() {
      this.exportToPDF();
    },

    async exportToPDF(documentId = null) {
      const docId = documentId || this.availableDocuments[0]?.id;
      if (!docId) {
        this.error = 'No document template available for download.';
        return;
      }

      this.downloadingDocument = true;
      try {
        const response = await downloadEvaluationDocument(docId, this.id);
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${this.summaryData?.title || 'evaluation'}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error downloading document:', error);
        this.error = 'Failed to download document. Please try again.';
      } finally {
        this.downloadingDocument = false;
      }
    }
  }
});
</script>

<style scoped>
.app-view {
  min-height: 100vh;
  width: 100%;
}

.evaluation-actions {
  display: flex;
  justify-content: flex-end;
  flex-direction: column;
  gap: 16px;
  margin-top: 32px;
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 10;
  background: rgba(255, 255, 255, 0.97);
  padding: 16px 24px;
  border-radius: 28px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
}

.evaluation-summary-view {
  background-color: #f5f5f5;
  min-height: 100vh;
  padding-bottom: 60px;
}

.summary-header-card {
  background: white;
  border-radius: 12px;
}

.summary-actions {
  display: flex;
  gap: 8px;
}

.info-item {
  text-align: center;
  padding: 16px;
  background: rgba(63, 81, 181, 0.05);
  border-radius: 8px;
  height: 100%;
}

.score-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 16px;
  overflow: hidden;
}

.score-display {
  position: relative;
}

.score-value {
  font-size: 4rem;
  color: white;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
}

.section-responses-card {
  border-radius: 12px;
  background: white;
}

.section-panel-header {
  background-color: #fafafa;
  border-radius: 8px;
  margin-bottom: 2px;
}

.section-content {
  padding: 16px 0;
}

.response-card {
  transition: all 0.3s ease;
}

.response-card:hover {
  transform: translateY(-2px);
}

.response-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}

.response-text {
  white-space: pre-wrap;
  line-height: 1.6;
  color: #424242;
}

.rating-response {
  display: flex;
  align-items: center;
}


.multi-field-response .v-simple-table td {
  padding: 8px 16px;
}

.comment-section {
  background-color: #f8f8f8;
  padding: 12px;
  border-radius: 8px;
}

.comment-text {
  color: #616161;
}


/* Print Styles */
@media print {
  .evaluation-summary-view {
    background-color: white;
    padding: 0;
  }

  .summary-actions,
  .v-btn,
  .v-chip {
    display: none !important;
  }

  .v-expansion-panel-content {
    display: block !important;
  }

  .v-expansion-panel-header {
    page-break-after: avoid;
  }

  .response-card {
    page-break-inside: avoid;
  }

  .score-card {
    background: none;
    border: 2px solid #667eea;
    color: #667eea;
  }

  .score-value {
    color: #667eea;
  }

  @page {
    margin: 1in;
  }
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .summary-header-card .d-flex {
    flex-direction: column;
    text-align: center;
  }

  .summary-actions {
    margin-top: 16px;
    justify-content: center;
  }

  .score-value {
    font-size: 3rem;
  }
}
</style>
